/*
Copyright 2026 ВИН СОЛЮШЕНС

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useState } from "react";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  builtInBackgroundUrl,
  CUSTOM_BACKGROUND,
  NO_BACKGROUND,
} from "./backgroundImages";
import { loadCustomBackground } from "./customBackground";

/**
 * Адрес картинки фона по выбранному имени.
 *
 * Встроенная лежит в сборке и адрес у неё постоянный. Своя лежит в IndexedDB,
 * и адрес для неё приходится создавать на время: `URL.createObjectURL` держит
 * ссылку на данные, пока её не отозвали, — поэтому отзываем при каждой смене,
 * иначе картинки копились бы в памяти вкладки до перезапуска.
 *
 * `null` — картинки нет или своя не читается.
 */
export function useBackgroundImageUrl(choice: string): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    choice === CUSTOM_BACKGROUND
      ? null
      : (builtInBackgroundUrl(choice) ?? null),
  );

  useEffect(() => {
    if (choice === NO_BACKGROUND) {
      setUrl(null);
      return;
    }

    if (choice !== CUSTOM_BACKGROUND) {
      setUrl(builtInBackgroundUrl(choice) ?? null);
      return;
    }

    let objectUrl: string | null = null;
    let dropped = false;

    loadCustomBackground()
      .then((blob) => {
        if (dropped) return;
        if (!blob) {
          setUrl(null);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((error) => {
        logger.error("Не удалось прочитать свою картинку фона", error);
        if (!dropped) setUrl(null);
      });

    return (): void => {
      dropped = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [choice]);

  return url;
}
